import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const topazVideoUpscaleParams = z
  .object({
    attachments: z.array(attachment).optional(),
    upscaleFactor: z.coerce.number().min(1).max(4).default(2),
    targetFps: z
      .union([z.literal("original"), z.coerce.number().min(16).max(60)])
      .optional(),
    codec: z.enum(["h265", "h264"]).default("h265"),
  })
  .strip();

export const topazVideoUpscale = defineModel({
  key: "topaz-video-upscale",
  stepType: "video",
  spec: {
    label: "Video Upscale",
    description:
      "Professional-grade video upscaling. Enhance your videos with high-quality upscaling up to 4x.",
    icon: "topaz-logo.svg",
    tags: ["video-edit", "upscaling", "high-res"],
    featured: true,
    info: "Upscaling a video with this model may take several minutes depending on video length.",
    features: {
      attachments: true,
      upscaleFactor: true,
      targetFps: true,
      codec: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/webm", "video/quicktime"],
          label: "Video",
          slot: "video",
          required: true,
        },
      ],
      upscaleFactor: {
        items: [
          { label: "1x", value: "1" },
          { label: "2x", value: "2" },
          { label: "3x", value: "3" },
          { label: "4x", value: "4" },
        ],
      },
      targetFps: {
        items: [
          { label: "Original", value: "original" },
          { label: "24 fps", value: "24" },
          { label: "30 fps", value: "30" },
          { label: "60 fps", value: "60" },
        ],
      },
      codec: {
        items: [
          { label: "H.265", value: "h265" },
          { label: "H.264", value: "h264" },
        ],
      },
    },
    params: topazVideoUpscaleParams,
  },
  providers: [
    {
      provider: "fal",
      modelId: "fal-ai/topaz/upscale/video",
      estimateCost(params) {
        const upscaleFactor = params.upscaleFactor ?? 2;
        const targetFps = params.targetFps;
        const DEFAULT_DURATION = 10;

        // Per-second rate by estimated output resolution (assuming 720p input)
        // 1x → ≤720p ($0.01/s), 2x+ → >1080p ($0.08/s)
        const costPerSec = upscaleFactor <= 1 ? 0.01 : 0.08;

        // Double if 60fps output (targetFps can be "original" or a number)
        const is60Fps = typeof targetFps === "number" && targetFps >= 60;
        const fpsMultiplier = is60Fps ? 2 : 1;

        return Math.ceil(
          costPerSec *
            fpsMultiplier *
            DEFAULT_DURATION *
            1_000_000 *
            (1 + BRAND_MARKUP),
        );
      },
      costPerSecond: 0.08 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const getUrl = (slot: string) =>
          params.attachments?.find((att) => att.slot === slot)?.data;

        const videoUrl = getUrl("video");

        if (!videoUrl) {
          throw new Error("Video URL is required for Topaz video upscale");
        }

        // Parse targetFps - "original" or undefined means no frame interpolation
        const targetFpsValue =
          params.targetFps !== undefined &&
          params.targetFps !== "original" &&
          typeof params.targetFps === "number"
            ? params.targetFps
            : undefined;

        return {
          model: this.modelId,
          prompt: "", // Topaz doesn't use prompts, but FalVideoPayload requires it
          video_url: videoUrl,
          upscale_factor: params.upscaleFactor ?? 2,
          ...(targetFpsValue !== undefined
            ? { target_fps: targetFpsValue }
            : {}),
          H264_output: params.codec === "h264",
        };
      },
    },
  ],
});
